/**
 * Terminal — reusable primitives for the command-center aesthetic.
 *
 *   <BlinkingCursor />        — single underscore, blinks like a CLI cursor
 *   <TypingText sequence />   — types through an array of strings, looping
 *   <TerminalChrome>...</TerminalChrome>  — macOS-window decoration around content
 *   <PromptLine>show failing tests</PromptLine>  — `> command` styled line
 */
import { useEffect, useState, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import clsx from 'clsx'

export function BlinkingCursor({ className }: { className?: string }) {
  return (
    <motion.span
      animate={{ opacity: [1, 1, 0, 0] }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear', times: [0, 0.5, 0.5, 1] }}
      className={clsx('inline-block', className)}
      aria-hidden="true"
    >
      _
    </motion.span>
  )
}

export function TerminalChrome({
  children,
  title = 'testops-command',
  className,
}: {
  children: ReactNode
  title?: string
  className?: string
}) {
  return (
    <div
      className={clsx(
        'overflow-hidden rounded-xl border border-white/10 bg-zinc-950 shadow-2xl shadow-black/50',
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b border-white/5 bg-zinc-900/80 px-4 py-2.5 backdrop-blur">
        <div className="flex gap-1.5">
          <span className="h-3 w-3 rounded-full bg-red-400/70" />
          <span className="h-3 w-3 rounded-full bg-yellow-400/70" />
          <span className="h-3 w-3 rounded-full bg-green-400/70" />
        </div>
        <div className="flex-1 text-center font-mono text-xs text-white/30">{title}</div>
        <div className="w-12" />
      </div>
      {children}
    </div>
  )
}

export function PromptLine({
  children,
  className,
  prefix = '>',
  prefixColor = 'text-cyan-400',
}: {
  children: ReactNode
  className?: string
  prefix?: string
  prefixColor?: string
}) {
  return (
    <div className={clsx('flex items-start gap-2 font-mono text-sm', className)}>
      <span className={clsx('select-none', prefixColor)}>{prefix}</span>
      <span className="flex-1 text-white/90">{children}</span>
    </div>
  )
}

interface TypingTextProps {
  sequence: string[]
  /** Milliseconds per character while typing. */
  typeSpeed?: number
  /** Milliseconds to pause once a string is fully typed. */
  holdDuration?: number
  /** Milliseconds per character while erasing. */
  eraseSpeed?: number
  className?: string
}

/**
 * TypingText — types through `sequence`, holds each, erases, types the next.
 * Designed for cycling a hero with example commands. Uses cumulative slicing
 * (no per-frame interval drift) so animations stay smooth even at 60fps.
 */
export function TypingText({
  sequence,
  typeSpeed = 45,
  holdDuration = 2200,
  eraseSpeed = 25,
  className,
}: TypingTextProps) {
  const [textIdx, setTextIdx] = useState(0)
  const [chars, setChars] = useState(0)
  const [isErasing, setIsErasing] = useState(false)

  useEffect(() => {
    const target = sequence[textIdx] || ''
    if (!isErasing && chars < target.length) {
      const t = setTimeout(() => setChars((c) => c + 1), typeSpeed)
      return () => clearTimeout(t)
    }
    if (!isErasing && chars >= target.length) {
      const t = setTimeout(() => setIsErasing(true), holdDuration)
      return () => clearTimeout(t)
    }
    if (isErasing && chars > 0) {
      const t = setTimeout(() => setChars((c) => c - 1), eraseSpeed)
      return () => clearTimeout(t)
    }
    if (isErasing && chars === 0) {
      setIsErasing(false)
      setTextIdx((i) => (i + 1) % sequence.length)
    }
  }, [chars, isErasing, textIdx, sequence, typeSpeed, holdDuration, eraseSpeed])

  return (
    <span className={className}>
      {(sequence[textIdx] || '').slice(0, chars)}
      <BlinkingCursor className="ml-0.5" />
    </span>
  )
}
